import { useEffect, useRef } from 'react';
import { useStore } from 'react-redux';
import { RootState } from '../app/store';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { MAX_CART_ITEM_QUANTITY } from '../constants';
import { addLoyaltiesCode, clearCart } from '../reducers/cartSlice';
import { addHypothesisItemToCart } from '../reducers/menuSlice';
import { orderActions } from '../reducers/orderSlice';
import { normalizeMenuModuleError } from '../utils/menuModuleError';
import { selectIsAIAutoMode } from '../redux/features/ai/ai.selector';
import {
  selectFinalOrderSubmitted,
  selectSessionId,
} from '../redux/features/sessionBoundary/sessionBoundary.selectors';
import { selectHypothesisFrame } from '../selectors/message';
import { resetGroupedUpsellDeclined } from '../utils/groupedUpsellDeclined';
import { getMenuUnificationFeatureFlag } from '../utils/session-storage';
import { EntityMenuItem } from '../reducers/messagingSlice';
import { convertBMOrderItemToEntityItem } from '../utils/menuUnification.utils';
import { CartItemAdditionTypes } from '../constants/event';
import { ICartLoyalty } from '../reducers/cartSlice.props';
import { selectBrandMenuConstraints } from '../selectors/menu';
import { selectFeatureFlagMenuModuleErrorUi } from '../redux/features/config/config.selector';
import logger from '../utils/logger';

const useHypothesisFrames = () => {
  const dispatch = useAppDispatch();
  // Non-subscribing store access: the cart-emptiness check below must read the
  // cart at frame-arrival time without re-running this effect on cart changes
  // (the replay itself mutates the cart, which would loop).
  const store = useStore<RootState>();
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const hypothesisFrame = useAppSelector(selectHypothesisFrame);
  const isFinalOrderSubmitted = useAppSelector(selectFinalOrderSubmitted);
  const sbSessionId = useAppSelector(selectSessionId);
  const brandMenuConstraints = useAppSelector(selectBrandMenuConstraints);
  const menuModuleErrorUiEnabled = useAppSelector(
    selectFeatureFlagMenuModuleErrorUi
  );

  const shouldAddHypothesisItems =
    isAIAutoMode &&
    hypothesisFrame &&
    hypothesisFrame.session_id === sbSessionId;

  const hitlBrandMenuMode = getMenuUnificationFeatureFlag();

  // Fingerprint of the last hypothesis content applied to the cart. The AI
  // re-sends hypothesis frames as the conversation progresses, usually with
  // unchanged content; rebuilding the cart for those frames is wasted work
  // that lags HITL on items with large constraint sets (e.g. Crowd Pleasers).
  const lastAppliedHypothesesRef = useRef('');

  useEffect(() => {
    if (!isFinalOrderSubmitted && shouldAddHypothesisItems) {
      // The flags change how a frame is applied, so a flip must invalidate the
      // fingerprint even when the hypothesis content is unchanged.
      const hypothesesFingerprint = [
        sbSessionId,
        hitlBrandMenuMode,
        menuModuleErrorUiEnabled,
        JSON.stringify(hypothesisFrame?.data?.hypotheses ?? null),
      ].join('::');
      if (hypothesesFingerprint === lastAppliedHypothesesRef.current) {
        const frameHasItems = !!hypothesisFrame?.data?.hypotheses?.some(
          (hypothesis) =>
            hypothesis.order_items?.length ||
            hypothesis.brand_menu_order_items?.length
        );
        const cartIsEmpty =
          Object.keys(store.getState().cart.cartItems).length === 0;
        // An agent can empty the cart (e.g. delete items in the cart review)
        // while auto mode stays on; an identical re-sent frame must then
        // rebuild the cart rather than be skipped.
        if (!frameHasItems || !cartIsEmpty) {
          logger.log(
            'Skipping hypothesis frame: content identical to the last applied frame'
          );
          return;
        }
      }
      lastAppliedHypothesesRef.current = hypothesesFingerprint;

      hypothesisFrame?.data?.hypotheses?.forEach((hypothesis) => {
        const {
          order_items: orderItems,
          brand_menu_order_items: brandMenuOrderItems,
          is_brand_menu: aiBrandMenuMode,
          loyalties = [],
          customer_info,
          menu_module_error: menuModuleError,
        } = hypothesis;

        dispatch(clearCart()); // Clear the cart and build a new one to avoid the comparison between cart items and order items when hypothesis evolves(update quantity or modifiers)
        resetGroupedUpsellDeclined(); // hypothesis replay rebuilds the cart from scratch, so suppress state must be recomputed from the new selectModifier calls
        dispatch(orderActions.setCustomerName('')); // Reset the customer name
        let skipFindInMenu = false;
        let orderItemsToUse: EntityMenuItem[] = orderItems || [];

        // Use brand menu cart:
        // 1. if both AI and HITL are using brand menu
        // 2. if pos cart is not available when AI uses brand menu but HITL uses PRP menu
        if (aiBrandMenuMode && hitlBrandMenuMode) {
          orderItemsToUse = convertBMOrderItemToEntityItem(
            brandMenuOrderItems || [],
            '',
            brandMenuConstraints
          );
        } else if (aiBrandMenuMode && !hitlBrandMenuMode) {
          // Skip find the hypothesis order item in the menu when AI and HITL are using different menu
          skipFindInMenu = true;
          if (orderItems && orderItems.length === 0) {
            // Use brand menu cart
            orderItemsToUse = convertBMOrderItemToEntityItem(
              brandMenuOrderItems || [],
              '',
              brandMenuConstraints
            );
          }
        }

        //Add loyalties added by AI in HITL cart
        loyalties.forEach((loyalty: ICartLoyalty) => {
          dispatch(
            addLoyaltiesCode({
              code: loyalty.code,
              addedBy: CartItemAdditionTypes.AI,
            })
          );
        });

        // Add customer info from AI hypothesis
        if (customer_info?.name) {
          dispatch(orderActions.setCustomerName(customer_info.name));
        }
        // Add items ordered by AI in HITL cart
        orderItemsToUse.forEach((orderItem) => {
          const { quantity } = orderItem;
          const transformedQuantity = Math.floor(quantity); //Added for temporary purpose only as quantity will never be in decimals

          dispatch(
            addHypothesisItemToCart({
              orderItem: {
                ...orderItem,
                quantity: Math.min(transformedQuantity, MAX_CART_ITEM_QUANTITY),
              },
              skipFindInMenu,
            })
          );
        });

        // Surface menu-module errors the AI reports for this hypothesis. The AI
        // only sends `menu_module_error` when the order failed to translate, so
        // an absent error clears any prior highlight (the AI may have corrected
        // the order). Errors are matched to the rebuilt cart by node name.
        // Gated by menu_module_error_ui (kill switch for the error surfacing).
        if (menuModuleErrorUiEnabled) {
          dispatch(
            orderActions.setMenuModuleCartErrors(
              menuModuleError ? normalizeMenuModuleError(menuModuleError) : []
            )
          );
        }
      });
    } else {
      // Outside auto mode the agent may edit the cart directly, so a later
      // identical frame must be re-applied rather than skipped.
      lastAppliedHypothesesRef.current = '';
    }
  }, [
    hypothesisFrame,
    isFinalOrderSubmitted,
    shouldAddHypothesisItems,
    hitlBrandMenuMode,
    menuModuleErrorUiEnabled,
  ]);
};

export default useHypothesisFrames;
